import type { CSSProperties } from 'react';

import { useMemo } from 'react';

import useElementResizeObserver from '@core/hooks/useElementResizeObserver';
import useEnvInfo from '@core/hooks/useEnvInfo';

/**
 * @returns a set of `CSSProperties` that can be provided to `@ui/Notification`
 * in order to position the notification relative to the bottom right corner of
 * the SuperHub editor's content area, rather than the bottom right corner of
 * the screen.
 *
 * @example
 *
 * ```jsx
 * const MyComponent = () => {
 *   const style = useEditorLayoutNotificationStyle();
 *
 *   useEffect(() => {
 *     notify(<Notification style={style}>Notification Content</Notification>)
 *   }, [])
 *
 *   // ...
 * }
 * ```
 */
export default function useEditorLayoutNotificationStyle(): CSSProperties {
  const { isClient } = useEnvInfo();

  // observe changes to the editor footer's width, since that means we need to
  // change the right margin of our notification
  const footerResizeResult = useElementResizeObserver(
    isClient ? document.querySelector('[class^="SuperHubEditorFormLayoutFooter"]') : null,
  );
  // observe changes to the document body's width, since we apply a max width to
  // the editor footer which means sometimes the footer's pixel position changes
  // on window resize even though the footer width did not change
  const bodyResizeResult = useElementResizeObserver(isClient ? document.body : null);

  const style: CSSProperties = useMemo(() => {
    const footerElement = footerResizeResult?.target;
    if (footerElement) {
      const footerStyle = isClient ? window.getComputedStyle(footerElement) : undefined;
      const footerRightBoundary = footerElement.getBoundingClientRect().right;
      const footerHeight = footerStyle?.getPropertyValue('--SuperHubEditor-footer-height') || '0px';
      const footerPadding = footerStyle?.getPropertyValue('--SuperHubEditor-footer-padding') || '0px';
      const superhubRightInset = footerStyle?.getPropertyValue('--SuperHub-margin') || '0px';
      const bodyWidth = bodyResizeResult?.width || 0;

      return {
        /**
         * If we want to position the notification (`[  ]`) in the bottom right
         * corner of the editor, its right margin needs to be the width of the
         * window, minus the inset we have between the window and editor, minus
         * the right boundary of the editor, plus the padding that the footer
         * uses to align it with the end of the footer's content.
         *
         * window
         * |-------------------------------------|
         * |     editor                    inset |
         * |     |-----------------------|       |
         * |     |                       |       |
         * |     |                       |       |
         * |     |                 [  ]  |       |
         * |     |-----------------------|       |
         * |     |                 Save  |       |
         * |     |-----------------------|       |
         * |-------------------------------------|
         */
        marginRight: `calc(${bodyWidth}px - ${superhubRightInset} - ${footerRightBoundary}px + ${footerPadding})`,
        /**
         * If we want to position the notification above the footer, its bottom
         * margin needs to be the height of the footer plus a small amount of
         * cushion.
         */
        marginBottom: `calc(${footerHeight} + var(--xs))`,
      };
    }

    return {};
  }, [isClient, footerResizeResult, bodyResizeResult]);

  return style;
}
